import { clearSupabaseCookies } from "@/lib/utils/auth/clear-cookies";
import { logger } from "@/lib/logger";
import type { SupabaseBrowserClient } from "./types";

interface CreateGoogleSignInHandlerArgs {
  supabase: SupabaseBrowserClient;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

export function createGoogleSignInHandler(args: CreateGoogleSignInHandlerArgs) {
  const { supabase, setIsLoading, setError, setSuccess } = args;

  return async (userType: "employee" | "client") => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Clear existing cookies before Google sign-in
      const clearResult = clearSupabaseCookies();
      logger.info("Cleared cookies before Google sign-in:", { clearResult });

      const currentOrigin = window.location.origin;
      const isLocalhost =
        currentOrigin.includes("localhost") || currentOrigin.includes("127.0.0.1");

      // CRITICAL: For OAuth, Supabase requires redirectTo to EXACTLY match
      // one of the Additional Redirect URLs (no query parameters allowed for matching)
      // We'll pass the origin and target path via the OAuth state instead
      const redirectUrl = `${currentOrigin}/auth/callback`;

      logger.info("🔗 Google OAuth redirect configuration:", {
        currentOrigin,
        isLocalhost,
        redirectUrl,
        userType,
        note: "Using clean URL without query params to match Supabase redirect URLs",
      });

      // Sign in with Google using Supabase's built-in OAuth (browser client)
      // Let Supabase handle the redirect (don't use skipBrowserRedirect)
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });

      if (error) {
        logger.error("Google sign-in error", error);
        setError(error.message);
        return;
      }

      // The redirect will happen automatically
      // No need to set success message as user will be redirected
    } catch (err) {
      logger.error("Unexpected Google sign-in error", err);
      setError("An unexpected error occurred during Google sign-in");
    } finally {
      setIsLoading(false);
    }
  };
}
